'use client';

import { useEffect, useRef } from 'react';
import Script from 'next/script';

const CLIENT_ID = process.env.NEXT_PUBLIC_CLEANHUB_CLIENT_ID;
const POLL_INTERVAL_MS = 200;
const POLL_TIMEOUT_MS = 3000;

// Module-level flag: prevents two instances on the same page from both
// calling CleanhubBanner.mount() (which would corrupt the widget state).
// Reset to false in the useEffect cleanup so the next PDP can mount fresh.
let mounted = false;

// Performs the actual DOM mount. Caller is responsible for verifying that
// window.CleanhubBanner.mount exists before calling this.
const doMount = () => {
  const container = document.getElementById('cleanhub-banner');
  if (container) container.innerHTML = '';

  window.CleanhubBanner.elements = new Map();
  window.CleanhubBanner.mount({ clientId: CLIENT_ID, elementId: 'cleanhub-banner' });

  // CleanHub renders an iframe asynchronously; give it 500ms to settle
  // before we patch the missing title attribute for accessibility.
  setTimeout(() => {
    const iframe = document.querySelector('.cleanhub-banner-wrapper iframe');
    if (iframe) {
      iframe.setAttribute('title', 'CleanHub sustainability certification banner');
    }
  }, 500);
};

// Polls every POLL_INTERVAL_MS until both the global and the container are
// available (the script's load event can fire before the global is fully
// defined, and the container is created by the effect below), then mounts
// the widget once.
// Returns a cancel function — call it from useEffect cleanup to stop a
// pending poll when the component unmounts.
const startPoll = () => {
  if (mounted) return () => {};

  let cancelled = false;
  let elapsed = 0;
  let intervalId = null;

  const tryMount = () => {
    // Also stops a poll whose cancel handle was discarded (onReady's return
    // value) once another poll on the page has already mounted the widget.
    if (cancelled || mounted) {
      clearInterval(intervalId);
      return;
    }

    if (!window.CleanhubBanner?.mount || !document.getElementById('cleanhub-banner')) {
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        console.error('[CleanHub] CleanhubBanner not available after timeout');
      }
      return;
    }

    clearInterval(intervalId);
    try {
      doMount();
      mounted = true;
    } catch (error) {
      console.error('[CleanHub] failed to mount CleanhubBanner', error);
    }
  };

  // Attempt immediately (covers the case where the global is already defined).
  tryMount();
  if (!mounted) intervalId = setInterval(tryMount, POLL_INTERVAL_MS);

  return () => {
    cancelled = true;
    if (intervalId) clearInterval(intervalId);
  };
};

const CleanHubWidget = () => {
  const wrapper = useRef(null);

  useEffect(() => {
    const banner = document.createElement('div');
    banner.id = 'cleanhub-banner';
    wrapper.current.appendChild(banner);

    // Navigation case: the user arrived at this PDP via client-side navigation
    // from another page that had already loaded CleanhubBanner.js. next/script
    // re-fire onReady on a remount (LoadCache hit), but it does so from
    // its own effect — and child effects run before ours, so that call finds
    // no container yet and falls back to polling. Starting the poll here too
    // mounts on this tick instead of waiting for the next one.
    const cancel = window.CleanhubBanner ? startPoll() : () => {};

    return () => {
      // Stop any in-flight poll and reset the flag so the next PDP mounts fresh.
      cancel();
      banner.remove();
      mounted = false;
    };
  }, []);

  return (
    <>
      <div ref={wrapper} className="max-w-4xl mx-auto my-10 min-h-37.5" />
      <Script
        src="https://cdn.cleanhub.io/embed/CleanhubBanner.js"
        strategy="lazyOnload"
        // onReady is the primary trigger for the initial page load: it fires
        // once the lazyOnload script has finished executing. We start the poll
        // here rather than in useEffect because lazyOnload fires well after
        // component mount, and a useEffect poll would time out waiting for it.
        onReady={startPoll}
        onError={(error) =>
          console.error('[CleanHub] failed to load CleanhubBanner script', error)
        }
      />
    </>
  );
};

export default CleanHubWidget;
